import { CheckCircleOutlined, EyeOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import { Button, LicensePlate } from "@rumbledirect/ui";
import { Alert, Card, Empty, Space, Typography, theme } from "antd";
import type { TableProps } from "antd";
import type { ReactNode } from "react";
import { Link, NavLink } from "react-router-dom";
import { Table } from "@/components/v2";
import type {
  DashboardAction,
  DashboardTableCardComponent,
} from "@rumbledirect/types";
import { getActionIcon } from "../actionHelpers";
import {
  getDataSourceAdapter,
  RECENT_UNREAD_DOSSIERS,
  type DashboardTableData,
  type DashboardTableRow,
} from "../dataSourceAdapters";

interface Props {
  component: DashboardTableCardComponent;
}

function renderCell(
  columnKey: string,
  record: DashboardTableRow,
): React.ReactNode {
  if (columnKey === "actions") {
    return (
      <Link to={record._href}>
        <Button icon={<EyeOutlined />} variant="light" />
      </Link>
    );
  }

  const value = record[columnKey];
  const display = value != null ? String(value) : "-";

  if (columnKey === "licensePlate") {
    return (
      <NavLink to={record._href}>
        <LicensePlate country="NL" size="small">
          {display}
        </LicensePlate>
      </NavLink>
    );
  }

  return display;
}

function buildColumns(
  configColumns: DashboardTableCardComponent["columns"],
): TableProps<DashboardTableRow>["columns"] {
  return configColumns.map((col) => ({
    key: col.key,
    title: col.label,
    dataIndex: col.key,
    render: (_: unknown, record: DashboardTableRow) =>
      renderCell(col.key, record),
  }));
}

function renderHeaderAction(action: DashboardAction): ReactNode {
  if (!action.url) {
    return null;
  }

  const icon = getActionIcon(action.icon) ?? undefined;
  const button = (
    <Button variant="light" icon={icon}>
      {action.text}
    </Button>
  );

  if (action.external) {
    return (
      <a href={action.url} target="_blank" rel="noopener noreferrer">
        {button}
      </a>
    );
  }

  return <Link to={action.url}>{button}</Link>;
}

export function TableCardRenderer({ component }: Props) {
  const { token } = theme.useToken();
  const adapter =
    component.dataSource != null
      ? getDataSourceAdapter(component.dataSource)
      : null;

  const dataSourceQuery = useQuery({
    queryKey: ["dashboard-datasource", component.dataSource ?? component.key],
    queryFn: () =>
      adapter
        ? adapter()
        : Promise.resolve<DashboardTableData>({ rows: [], total: 0 }),
    enabled: adapter != null,
  });

  const rows: DashboardTableRow[] =
    adapter != null
      ? (dataSourceQuery.data?.rows ?? [])
      : (component.rows as DashboardTableRow[]);

  const total =
    adapter != null ? (dataSourceQuery.data?.total ?? 0) : rows.length;

  const overflowCount = total - rows.length;
  const overflowHref = component.headerAction?.url ?? null;

  const columns = buildColumns(component.columns);

  const showCaughtUp =
    component.dataSource === RECENT_UNREAD_DOSSIERS &&
    !dataSourceQuery.isLoading &&
    !dataSourceQuery.isError &&
    rows.length === 0;

  return (
    <Card
      title={component.title}
      extra={
        component.headerAction != null
          ? renderHeaderAction(component.headerAction)
          : undefined
      }
      loading={adapter != null ? dataSourceQuery.isLoading : false}
    >
      {dataSourceQuery.isError ? (
        <Alert
          type="error"
          message="Ongelezen dossiers konden niet worden geladen. Probeer de pagina te vernieuwen."
          style={{ marginBottom: 16 }}
        />
      ) : null}
      {showCaughtUp ? (
        <Empty
          image={
            <CheckCircleOutlined
              style={{ fontSize: 56, color: token.colorSuccess }}
            />
          }
          description={
            <Space orientation="vertical" size={2}>
              <Typography.Text strong style={{ fontSize: 16 }}>
                Je bent helemaal bij!
              </Typography.Text>
              <Typography.Text type="secondary">
                Er zijn geen ongelezen dossiers.
              </Typography.Text>
            </Space>
          }
          style={{ padding: "32px 0" }}
        />
      ) : (
        <>
          <Table<DashboardTableRow>
            dataSource={rows}
            columns={columns}
            rowKey="_id"
            pagination={false}
            size="small"
          />
          {overflowCount > 0 && overflowHref != null ? (
            <div style={{ marginTop: 12, textAlign: "center" }}>
              <Link to={overflowHref}>
                +{overflowCount} andere {component.title.toLowerCase()}
              </Link>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}
